import { FieldErrors, FormProvider } from 'react-hook-form'
import React from 'react'
import * as yup from 'yup'
import { MobileSettings } from '@matech/thebigpos-sdk'
import { useIntl } from 'react-intl'
import InfoIcon from '@mui/icons-material/Info'
import { TextField } from '../../formControls'
import useForm from '../../../hooks/useForm'
import FormRow from '../../formControls/FormRow'
import { Button } from '../../Button'
import Switch from '../../formControls/Switch'
import TextTooltip from '../../tooltips/TextTooltip'

export type MobileSettingsFormFieldValues = Omit<MobileSettings, 'id'>

export type MobileSettingsFormProps = {
	initialValues?: MobileSettingsFormFieldValues
	disabled: boolean
	onSubmitSuccess?: (data: MobileSettingsFormFieldValues) => void
	onSubmitError?: (
		errors: FieldErrors<MobileSettingsFormFieldValues>
	) => void
}

const schema = yup.object().shape({
	hasMobile: yup.boolean(),
	universalUrl: yup
		.string()
		.url('Must be a valid URL')
		.required('Download link is required'),
	appleStoreUrl: yup
		.string()
		.url('Must be a valid URL')
		.required('Apple Store link is required'),
	googlePlayStoreUrl: yup
		.string()
		.url('Must be a valid URL')
		.required('Google Play Store link is required'),
})

const defaultValues: MobileSettingsFormFieldValues = {
	hasMobile: false,
	universalUrl: '',
	appleStoreUrl: '',
	googlePlayStoreUrl: '',
}

const MobileSettingsForm = ({
	initialValues,
	disabled,
	onSubmitSuccess,
	onSubmitError,
}: MobileSettingsFormProps) => {
	const methods = useForm<MobileSettingsFormFieldValues>({
		defaultValues: initialValues ?? defaultValues,
		values: initialValues ?? defaultValues,
		schema,
	})

	const { handleSubmit } = methods

	const handleFormSubmit = (data: MobileSettingsFormFieldValues) => {
		onSubmitSuccess?.(data)
	}

	const handleSubmitClick = () => {
		handleSubmit(handleFormSubmit, onSubmitError)()
	}

	const hasMobile = methods.watch('hasMobile')
	const fieldsDisabled = disabled || !hasMobile

	const { formatMessage } = useIntl()

	return (
		<FormProvider {...methods}>
			<FormRow className="flex flex-col gap-2">
				<Switch
					name="hasMobile"
					label={formatMessage({
						id: 'mobile_settings.has_mobile_switch',
					})}
					defaultChecked={false}
					disabled={disabled}
				/>
				<TextField
					name="universalUrl"
					label={formatMessage({
						id: 'mobile_settings.mobile_app_universal_url',
					})}
					placeholder={formatMessage({
						id: 'mobile_settings.mobile_app_enter_url',
					})}
					fullWidth
					disabled={fieldsDisabled}
					helperText={
						<TextTooltip
							title={formatMessage({
								id: 'mobile_settings.mobile_app_universal_url_desc',
							})}
						>
							<InfoIcon sx={{ fontSize: 16, marginRight: '2px' }} />
							{formatMessage({
								id: 'mobile_settings.what_is_universal_url',
							})}
						</TextTooltip>
					}
				/>
				<TextField
					name="appleStoreUrl"
					label={formatMessage({
						id: 'mobile_settings.mobile_app_app_store_url',
					})}
					placeholder={formatMessage({
						id: 'mobile_settings.mobile_app_enter_url',
					})}
					fullWidth
					disabled={fieldsDisabled}
				/>
				<TextField
					name="googlePlayStoreUrl"
					label={formatMessage({
						id: 'mobile_settings.mobile_app_google_play_url',
					})}
					placeholder={formatMessage({
						id: 'mobile_settings.mobile_app_enter_url',
					})}
					fullWidth
					disabled={fieldsDisabled}
				/>
			</FormRow>
			{!disabled && (
				<Button
					text={formatMessage({ id: 'global.submit' })}
					onClick={handleSubmitClick}
				/>
			)}
		</FormProvider>
	)
}
export default MobileSettingsForm
